import React from "react";
import {
  HardHat, Briefcase, Wrench, ShieldAlert, Bell, FileText,
  BarChart3, Archive, ClipboardCheck, GraduationCap, Truck,
  Users, ArrowRight,
} from "lucide-react";

/**
 * LessonRouteStrip
 * --------------------------------------------------
 * A restrained, industrial mini-chain shown inside each LessonCard
 * that visually answers: "where does this lesson's action ROUTE?"
 *
 * Inferred from lesson slug — no data changes required. If we can't
 * infer a meaningful chain, we render nothing (better than a generic
 * filler).
 *
 * Goal: replace some of the "wall of text" feeling with a
 * Palantir-style operational diagram that shows the platform reacting.
 */

const NODE_TONE = {
  field:    "#FF6A00",
  pm:       "#FFFFFF",
  shop:     "#FFB000",
  safety:   "#FF6A6A",
  pdf:      "#FFFFFF",
  kpi:      "#65DA76",
  archive:  "#65DA76",
  training: "#FF6A00",
};

/**
 * Map slug patterns to operational chains. Order matters — first match wins.
 */
const ROUTE_RULES = [
  // Daily report → PM → PDF → KPI → Archive
  {
    test: /daily.?report/,
    label: "DAILY REPORT · ROUTING",
    nodes: [
      { tone: "field",    icon: HardHat,   actor: "Field Crew",  step: "Submit" },
      { tone: "pm",       icon: Briefcase, actor: "PM Console",  step: "Notified" },
      { tone: "pdf",      icon: FileText,  actor: "PDF Render",  step: "Branded PDF" },
      { tone: "kpi",      icon: BarChart3, actor: "Exec KPIs",   step: "Roll-forward" },
      { tone: "archive",  icon: Archive,   actor: "Archive",     step: "Locked" },
    ],
  },
  // Equipment / pre-op → Shop → Lockout → Repair → RTS
  {
    test: /(equipment|pre.?op|inspection.*equip|truck|vehicle|rts)/,
    label: "PRE-OP RESULT · ROUTING",
    nodes: [
      { tone: "field",   icon: HardHat,        actor: "Operator",  step: "Pre-Op submitted" },
      { tone: "shop",    icon: ShieldAlert,    actor: "System",    step: "If FAIL · lockout" },
      { tone: "shop",    icon: Wrench,         actor: "Shop",      step: "Repair queue" },
      { tone: "kpi",     icon: ClipboardCheck, actor: "RTS Log",   step: "Return-to-Service" },
    ],
  },
  // Incident / near-miss / safety
  {
    test: /(incident|near.?miss|injury|safety|jha)/,
    label: "INCIDENT · ESCALATION CHAIN",
    nodes: [
      { tone: "field",    icon: ShieldAlert,    actor: "Field",     step: "Incident submitted" },
      { tone: "safety",   icon: Bell,           actor: "Safety",    step: "Paged" },
      { tone: "pm",       icon: ClipboardCheck, actor: "CA",        step: "Auto-generated" },
      { tone: "training", icon: GraduationCap,  actor: "Training",  step: "Auto-assigned" },
      { tone: "kpi",      icon: BarChart3,      actor: "Exec",      step: "Same-day visibility" },
    ],
  },
  // Toolbox talk / meeting
  {
    test: /(toolbox|meeting|talk|huddle)/,
    label: "TOOLBOX TALK · ROUTING",
    nodes: [
      { tone: "field",   icon: Users,          actor: "Crew",      step: "Sign-in (QR)" },
      { tone: "pm",      icon: Briefcase,      actor: "PM",        step: "Attendance routed" },
      { tone: "pdf",     icon: FileText,       actor: "PDF",       step: "Acknowledgment doc" },
      { tone: "archive", icon: Archive,        actor: "Crew File", step: "Logged per op" },
    ],
  },
  // Materials / deliveries / parts
  {
    test: /(material|deliver|parts|order)/,
    label: "MATERIALS · ROUTING",
    nodes: [
      { tone: "field",  icon: Truck,          actor: "Field",     step: "Receipt logged" },
      { tone: "pm",     icon: Briefcase,      actor: "PM",        step: "Cost coded" },
      { tone: "kpi",    icon: BarChart3,      actor: "Exec",      step: "Job cost roll-up" },
    ],
  },
  // QA/QC / inspection (non-equipment)
  {
    test: /(qa.?qc|qaqc|quality)/,
    label: "QA / QC · ROUTING",
    nodes: [
      { tone: "field",   icon: ClipboardCheck, actor: "Field",     step: "Inspection logged" },
      { tone: "pm",      icon: Briefcase,      actor: "PM",        step: "Reviewed" },
      { tone: "archive", icon: Archive,        actor: "Archive",   step: "Pass/fail trail" },
    ],
  },
  // Backup / restore / admin platform ops
  {
    test: /(backup|restore|reseed|reset|recovery)/,
    label: "ADMIN · SYSTEM RECOVERY",
    nodes: [
      { tone: "pm",      icon: Briefcase,      actor: "Admin",     step: "Triggers" },
      { tone: "archive", icon: Archive,        actor: "Snapshot",  step: "Off-site .zip" },
      { tone: "kpi",     icon: ClipboardCheck, actor: "Audit",     step: "Trail logged" },
    ],
  },
];

export default function LessonRouteStrip({ lesson }) {
  if (!lesson?.slug) return null;
  const rule = ROUTE_RULES.find((r) => r.test.test(lesson.slug));
  if (!rule) return null;

  return (
    <div
      className="rounded-md p-4 sm:p-5 print:bg-white print:border-slate-400 print:text-slate-900"
      style={{
        background: "#000",
        border: "1px solid #1A1A1A",
      }}
      data-testid={`lesson-route-${lesson.slug}`}
    >
      <div
        className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold mb-3 print:text-slate-500"
        style={{ color: "#FF6A00" }}
      >
        {rule.label}
      </div>

      <div className="flex items-stretch gap-1.5 sm:gap-2 flex-wrap">
        {rule.nodes.map((n, i) => {
          const Icon = n.icon;
          const tone = NODE_TONE[n.tone] || "#FFFFFF";
          const isLast = i === rule.nodes.length - 1;
          return (
            <React.Fragment key={i}>
              <div
                className="flex items-center gap-2 px-2.5 py-2 rounded print:bg-white print:text-slate-900 print:border-slate-300"
                style={{
                  background: "#0F0F0F",
                  border: "1px solid #1A1A1A",
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded shrink-0 print:bg-white print:text-slate-900 print:border-slate-400"
                  style={{
                    background: `${tone}1F`,
                    color: tone,
                    border: `1px solid ${tone}55`,
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0">
                  <div
                    className="font-mono text-[9px] uppercase tracking-[0.2em] font-bold truncate print:text-slate-500"
                    style={{ color: "#666" }}
                  >
                    {n.actor}
                  </div>
                  <div
                    className="font-mono text-[10px] truncate print:text-slate-800"
                    style={{ color: "#FFFFFF" }}
                  >
                    {n.step}
                  </div>
                </div>
              </div>
              {!isLast && (
                <div
                  className="flex items-center justify-center"
                  style={{ flex: "0 0 auto" }}
                  aria-hidden
                >
                  <ArrowRight
                    className="w-3.5 h-3.5"
                    style={{ color: "#FF6A00", opacity: 0.5 }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
